import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartSimple } from '@fortawesome/pro-light-svg-icons/faChartSimple';
import { faShareNodes } from '@fortawesome/pro-light-svg-icons/faShareNodes';
import { faLink } from '@fortawesome/pro-light-svg-icons/faLink';
import { faCreditCard } from '@fortawesome/pro-light-svg-icons/faCreditCard';
import { faEnvelope } from '@fortawesome/pro-light-svg-icons/faEnvelope';
import { faLock } from '@fortawesome/pro-light-svg-icons/faLock';
import { faPaperPlane } from '@fortawesome/pro-light-svg-icons/faPaperPlane';
import { faTrophy } from '@fortawesome/pro-light-svg-icons/faTrophy';
import { TypedMemo } from '../../../common/types';
import { notificationsMessages as m } from './notificationsMessages';
import type { NotificationPreferences, NotificationPreferenceKey } from './useNotificationPreferences';
import { SwitchRow } from './SwitchRow';
import { SongsBrewNotificationGroup } from './SongsBrewNotificationGroup';
import type { SettingsSaveState } from '../SettingsSaveStatus';
import { Panel, PanelFoot, PanelHead } from '../../../music24/components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Settings tabs; each part uses its own slice of it */
import cards from '../settingsCards.module.scss';
/* eslint-enable css-modules/no-unused-class */

const messages = defineMessages({
  yours: { id: 'app.sml.nav.group.yours', defaultMessage: 'Your intelligence' },
  promote: { id: 'app.sml.sidebar.section.promote', defaultMessage: 'Promote' },
  postPack: {
    id: 'app.sml.settings.notifications.postPackEmails.toggle.label',
    defaultMessage: 'Post pack and kit emails',
  },
  postPackDescription: {
    id: 'app.sml.settings.notifications.postPackEmails.toggle.description',
    defaultMessage:
      'A scheduled post we cannot publish for you, ready to share by hand. Also the note that your release kit is ready.',
  },
  presaveMilestones: {
    id: 'app.sml.settings.notifications.presaveMilestones.toggle.label',
    defaultMessage: 'Pre-save milestones',
  },
  presaveMilestonesDescription: {
    id: 'app.sml.settings.notifications.presaveMilestones.toggle.description',
    defaultMessage: 'An email when 10, 100 and 1000 fans pre-save a release.',
  },
  outreach: { id: 'app.sml.nav.group.outreach', defaultMessage: 'Outreach' },
  outreachDescription: {
    id: 'app.sml.settings.notifications.outreach.description',
    defaultMessage: 'Pitches to curators, press and radio.',
  },
  outreachPush: {
    id: 'app.sml.settings.notifications.outreachPush.toggle.label',
    defaultMessage: 'Replies and placements',
  },
  outreachPushDescription: {
    id: 'app.sml.settings.notifications.outreachPush.toggle.description',
    defaultMessage: 'A note in the MUSIC24 app when a curator answers a pitch or a playlist adds your song.',
  },
  plan: { id: 'app.sml.settings.notifications.planEmails.toggle.label', defaultMessage: 'Plan and trial emails' },
  planDescription: {
    id: 'app.sml.settings.notifications.planEmails.toggle.description',
    defaultMessage: 'A reminder before your trial ends, a note when your plan changes, and tips for the Free plan.',
  },
  productUpdates: {
    id: 'app.sml.settings.notifications.productUpdates.toggle.label',
    defaultMessage: 'Product updates',
  },
  productUpdatesDescription: {
    id: 'app.sml.settings.notifications.productUpdates.toggle.description',
    defaultMessage: 'One email when we release a new version of MUSIC24: what changed and how to get it.',
  },
});

interface Props {
  prefs: NotificationPreferences;
  update: <K extends NotificationPreferenceKey>(key: K, value: NotificationPreferences[K]) => void;
  /** The SongsBrew switch saves through the newsletter router and shares the tab head status. */
  setSaveState: (state: SettingsSaveState) => void;
}

/**
 * Your intelligence (no email yet), Promote, Outreach (plan 2026-10-11 Task 9), Account and plan (design settings-redesign c1) and Newsletter (design:
 * songsbrew-promotion c1; only when the SongsBrew platform is set up).
 * Each product label covers one box, so it is the head of that box.
 * The plan, product updates, post pack and pre-save milestone switches are the only way back after a one-click
 * unsubscribe.
 */
const OtherNotificationGroupsComponent: React.FC<Props> = ({ prefs, update, setSaveState }) => {
  const intl = useIntl();
  return (
    <>
      <Panel as="section">
        <PanelHead title={intl.formatMessage(messages.yours)} subtitle={intl.formatMessage(m.yoursDescription)} />
        <p className={`${cards.muted} mb-0`}>
          <FontAwesomeIcon icon={faChartSimple} />
          {intl.formatMessage(m.yoursNone)}
        </p>
      </Panel>

      <Panel as="section">
        <PanelHead title={intl.formatMessage(messages.promote)} subtitle={intl.formatMessage(m.promoteDescription)} />
        <SwitchRow
          icon={faShareNodes}
          title={intl.formatMessage(messages.postPack)}
          description={intl.formatMessage(messages.postPackDescription)}
          checked={prefs.postPackEmailsEnabled}
          onChange={(value) => update('postPackEmailsEnabled', value)}
        />
        <div className={cards.row}>
          <span className={cards.rowIcon}>
            <FontAwesomeIcon icon={faLink} />
          </span>
          <div className={cards.rowText}>
            <span className={cards.rowTitle}>{intl.formatMessage(m.presaveTitle)}</span>
            <span className={cards.rowSub}>{intl.formatMessage(m.presaveDescription)}</span>
          </div>
          <div className={cards.rowEnd}>
            <span className={cards.pill}>{intl.formatMessage(m.alwaysOn)}</span>
          </div>
        </div>
        <SwitchRow
          icon={faTrophy}
          title={intl.formatMessage(messages.presaveMilestones)}
          description={intl.formatMessage(messages.presaveMilestonesDescription)}
          checked={prefs.presaveMilestoneEmailsEnabled}
          onChange={(value) => update('presaveMilestoneEmailsEnabled', value)}
        />
      </Panel>

      <Panel as="section">
        <PanelHead
          title={intl.formatMessage(messages.outreach)}
          subtitle={intl.formatMessage(messages.outreachDescription)}
        />
        <SwitchRow
          icon={faPaperPlane}
          title={intl.formatMessage(messages.outreachPush)}
          description={intl.formatMessage(messages.outreachPushDescription)}
          checked={prefs.pushOutreachEnabled}
          onChange={(value) => update('pushOutreachEnabled', value)}
        />
      </Panel>

      <Panel as="section">
        <PanelHead title={intl.formatMessage(m.accountTitle)} />
        <SwitchRow
          icon={faCreditCard}
          title={intl.formatMessage(messages.plan)}
          description={intl.formatMessage(messages.planDescription)}
          checked={prefs.planEmailsEnabled}
          onChange={(value) => update('planEmailsEnabled', value)}
        />
        {/* The release notes mail (design whats-new a7, a8). On by default. */}
        <SwitchRow
          icon={faEnvelope}
          title={intl.formatMessage(messages.productUpdates)}
          description={intl.formatMessage(messages.productUpdatesDescription)}
          checked={prefs.productUpdates}
          onChange={(value) => update('productUpdates', value)}
        />
        <PanelFoot>
          <FontAwesomeIcon icon={faLock} />
          <span>{intl.formatMessage(m.alwaysSent)}</span>
        </PanelFoot>
      </Panel>

      <SongsBrewNotificationGroup setSaveState={setSaveState} />
    </>
  );
};

export const OtherNotificationGroups = TypedMemo(OtherNotificationGroupsComponent);
